/**
 * TRAC-790 — "you are on your way to a person", as a pure derivation.
 *
 * On a non-tire page (or when the caller asked for a person) the chat opens on the
 * connect-to-a-person menu. While that menu is the state of play, the whole widget
 * should read as human: the header wears a headset and says Chat, the doors are red,
 * and the red "Connect with a Tire Expert" button stays out of it — those doors already
 * ARE the connect path, so leaving the button up asked the same question twice and
 * answered it with the same four options.
 *
 * The state is read off the newest thing the chat SAID, not off a latch. A menu is
 * still on the table right up until the chat says something that isn't a menu — which
 * is exactly when the customer has turned it into an AI conversation. Doing it this way
 * survives the second menu behind "My orders": picking a door sends a customer turn, so
 * anything keyed on "has the customer sent a message" drops out of human dress at the
 * moment the customer is going deeper INTO the connect flow.
 *
 * Extracted from ChatInterface so the rules are unit-testable without mounting the
 * widget, same as chatBanner.ts.
 */
import type { Message } from '../../types/chat.types';
import type { HandoffState } from './chatBanner';

/** A message from the chat offering doors to a person (the opening menu, the orders submenu). */
export function isRoutingMenu(m: Message): boolean {
  return m.role === 'assistant' && (m.parts || []).some((p) => p.type === 'routing_actions');
}

/**
 * Is a connect-to-a-person menu the newest thing the chat said?
 *
 * Dividers and band markers ride the assistant role too and say nothing to the
 * customer, so they're stepped over rather than counted as an answer.
 */
export function isHumanFirstMode(messages: Message[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== 'assistant') continue;
    if (m.dividerKind) continue;
    return isRoutingMenu(m);
  }
  return false;
}

export interface BannerSuppression {
  humanFirstMode: boolean;
  handoffState: HandoffState;
  /** liveChatAvail.state — 'checking' | 'open' | anything-else (closed/unknown). */
  availState: string;
  /** routingPending — the customer asked for a person and hasn't been routed yet. */
  routingPending: boolean;
}

/**
 * Should the red "Connect with a Tire Expert" banner be hidden?
 *
 * Only while a menu is the live offer, only before any handoff, and only while live
 * chat is actually reachable:
 *  - `routingPending` means the customer asked for a person from the BUTTON, and the
 *    button is now their "Cancel request" escape hatch. Taking it away would strand
 *    them in a request they can't back out of.
 *  - Outside LP hours the banner earns its place even here: it's the one thing that
 *    says when we reopen, and it opens the hours modal rather than a second copy of
 *    the menu, so nothing is duplicated. 'checking' counts as reachable so the button
 *    can't flash in and straight back out while availability resolves.
 */
export function hideModeBanner(
  { humanFirstMode, handoffState, availState, routingPending }: BannerSuppression,
): boolean {
  const liveChatClosed = availState !== 'open' && availState !== 'checking';
  return humanFirstMode && handoffState === 'idle' && !routingPending && !liveChatClosed;
}
